import { useMemo } from "react";
import { useRootStore } from "@/state/RootStore";
import type { Curriculum } from "@/state/QuestionsStore";
import type { Topic } from "@/components/ai-chat/types";
import type { QuestionGuideCategory, QuestionGuideRecord } from "@/types/questionGuides";
import { normalizeQuestionGuide, questionGuidesBaseUrl } from "./questionGuideData";

function resolveCurriculumFraction(
  value: Curriculum[string]["per_of_q"] | undefined,
  selectedStateCode: string,
): number | undefined {
  if (typeof value === "number") return value;
  if (!value || !selectedStateCode) return undefined;
  const fraction = value[selectedStateCode];
  return typeof fraction === "number" ? fraction : undefined;
}

export function useQuestionGuides(): QuestionGuideCategory[] {
  const store = useRootStore();
  const rawGuides = store.mainAppStore.questionGuides;
  const recommendedTopics = store.mainAppStore.recommendedTopicsForAI as Record<string, Topic>;
  const curriculum = store.questionsStore.curriculum;
  const selectedStateCode = store.mainAppStore.selectedStateCode || "";

  return useMemo(() => {
    if (!questionGuidesBaseUrl) return [];

    const topicSectionIds = new Map<string, string>();
    for (const topic of Object.values(recommendedTopics)) {
      const topicName = topic?.name?.trim().toLocaleLowerCase();
      const sectionId = topic?.sectionId?.trim();
      if (topicName && sectionId) topicSectionIds.set(topicName, sectionId);
    }

    const groupedGuides = new Map<string, { title: string; guides: QuestionGuideRecord[] }>();
    for (const value of Object.values(rawGuides)) {
      const guide = normalizeQuestionGuide(value);
      if (!guide) continue;
      const categoryKey = guide.category.toLocaleLowerCase();
      const group = groupedGuides.get(categoryKey);
      if (group) group.guides.push(guide);
      else groupedGuides.set(categoryKey, { title: guide.category, guides: [guide] });
    }

    return Array.from(groupedGuides.entries(), ([id, group]) => {
      const percentages = new Set<number>();
      for (const guide of group.guides) {
        const sectionId = topicSectionIds.get(guide.topic.toLocaleLowerCase());
        if (!sectionId) continue;
        const fraction = resolveCurriculumFraction(
          curriculum[sectionId]?.per_of_q,
          selectedStateCode,
        );
        if (fraction && fraction > 0 && fraction <= 1) {
          percentages.add(Math.round(fraction * 100));
        }
      }

      return {
        id,
        title: group.title,
        examWeight: percentages.size === 1 ? [...percentages][0] : undefined,
        guides: group.guides.map((guide) => ({
          id: guide.slug,
          title: guide.topic,
          url: `${questionGuidesBaseUrl}${guide.slug}`,
          readTimeMinutes: guide.readTimeMinutes,
        })),
      };
    });
  }, [curriculum, rawGuides, recommendedTopics, selectedStateCode]);
}
